import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import ProfileTable from "./profile-table";
import ProfileOrderList from "./profile-order-list";
import ProfileCard from "./profile-card/profile-card";
import ApiProfileManagement from "../../../api/profile-management";
import useNotification from "../../../components/notification/notification";
import "./styles.css";
import { Body } from "../../../components/main-body/body";
import { Modal } from "antd";
import { useParams, useLocation } from "react-router-dom";
import { useCan } from "../../../common/utils/usePermissions";
import { useProjectNavigate } from "../../../common/utils/useProjectNavigate";
import DropdownFilters from "../../../components/dropdown-filters/dropdown-filters";
import debounce from "../../../common/utils/debounce";
import { useBulkSelection } from "../../../common/utils/useBulkSelection";
import BulkSelectionBar from "../../../components/table/bulk-selection-bar";
import useAdminEvents from "../../../hooks/useAdminEvents";

const DeleteModalComponent = ({ title, handleOk, handleCancel, isModalOpen }) => (
  <Modal
    title={title}
    open={isModalOpen}
    onOk={handleOk}
    onCancel={handleCancel}
    okButtonProps={{ danger: true }}
    okText="Delete"
    className="plan-page-action-modal"
  >
    <p>Are you sure you want to delete this profile field?</p>
  </Modal>
);

const SORT_FIELDS = {
  title: "title",
  inputType: "inputType",
  status: "status",
  updatedAt: "updatedAt",
};

const ProfileManagement = ({ mode }) => {
  const [profiles, setProfiles] = useState([]);
  const [isOpen, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [scenarioData, setScenarioData] = useState({});
  const [selectedProfile, setSelectedProfile] = useState({});
  const { t } = useTranslation();
  const { openNotification } = useNotification();
  const [isOpenDeleteProfileModal, setOpenDeleteProfileModal] = useState();
  const { id } = useParams();
  const navigate = useProjectNavigate();
  const location = useLocation();
  const [isBulkDeleting, setBulkDeleting] = useState(false);

  const tableRef = useRef(null);
  const [bulkLeft, setBulkLeft] = useState(null);

  const {
    selectedKeys,
    clearSelection,
    getSelectionColumn,
    setSelectedKeys,
  } = useBulkSelection(profiles, { rowKey: "id" });

  const [sort, setSort] = useState({ field: null, order: null });

  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({});

  // Edit-order (drag & drop) mode for the subject profile fields (LF360-1734).
  const [isOrdering, setIsOrdering] = useState(false);
  const [orderList, setOrderList] = useState([]);
  const [initialOrderIds, setInitialOrderIds] = useState([]);
  const [savingOrder, setSavingOrder] = useState(false);

  const handleNavigate = (path) => {
    if (location.pathname !== path) navigate(path);
  };

  // LF360-1912: `manageable` leaves out the platform's own fields. They are
  // listed by the endpoint for every picker that asks what profile fields
  // exist, but nothing about them can be edited, reordered or deleted, so this
  // screen has nothing to show for them.
  const getProfiles = useCallback(async (params = {}) => {
    const { data } = await ApiProfileManagement.getProfiles({ ...params, manageable: true });
    const { subjectProfileFields } = data || {};
    setProfiles(subjectProfileFields || []);
  }, []);

  const debouncedGetProfiles = useMemo(
    () => debounce(getProfiles, 400),
    [getProfiles]
  );

  const apiParams = useMemo(() => {
    const rawTypes = filters?.inputType;
    const rawStatuses = filters?.status;
    const rawDisplayRule = filters?.displayRule;

    const inputType = Array.isArray(rawTypes) && rawTypes.length ? rawTypes : undefined;
    const status = Array.isArray(rawStatuses) && rawStatuses.length ? rawStatuses : undefined;
    const displayRule = Array.isArray(rawDisplayRule) && rawDisplayRule.length ? rawDisplayRule : undefined;

    return {
      ...(search.trim() ? { search: search.trim() } : {}),
      ...(inputType ? { inputType } : {}),
      ...(status ? { status } : {}),
      ...(displayRule ? { displayRule } : {}),
      sortBy: sort.field,
      sortOrder: sort.order,
    };
  }, [filters, search, sort.field, sort.order]);

  const orderDirty = useMemo(
    () => JSON.stringify(orderList.map((f) => f.id)) !== JSON.stringify(initialOrderIds),
    [orderList, initialOrderIds]
  );

  const enterOrdering = useCallback(async () => {
    // Reorder the full set in saved order, ignoring active search/filters.
    const { data } = await ApiProfileManagement.getProfiles({
      sortBy: "position",
      sortOrder: "ASC",
      manageable: true,
    });
    const fields = data?.subjectProfileFields || [];
    setOrderList(fields);
    setInitialOrderIds(fields.map((f) => f.id));
    setIsOrdering(true);
  }, []);

  const cancelOrdering = useCallback(() => {
    setIsOrdering(false);
    setOrderList([]);
    setInitialOrderIds([]);
  }, []);

  const saveOrder = useCallback(async () => {
    if (!orderDirty) {
      setIsOrdering(false);
      return;
    }
    setSavingOrder(true);
    try {
      await ApiProfileManagement.reorderProfiles(orderList.map((f) => f.id));
      setIsOrdering(false);
      await getProfiles(apiParams);
      openNotification({
        type: "success",
        message: <p>{t("subject_management.profile.notification.order_updated")}</p>,
      });
    } catch (e) {
      openNotification({
        type: "error",
        message: <p>{t("subject_management.profile.notification.error")}</p>,
      });
    } finally {
      setSavingOrder(false);
    }
  }, [orderDirty, orderList, getProfiles, apiParams, openNotification, t]);

  // Adding and reordering fields need `settings.profile.edit` (LF360-2024).
  const canManage = useCan("settings.profile.edit");

  const headerProps = useMemo(
    () => (isOrdering
      ? {
        title: "Subject profile",
        cancel: { label: "Cancel", onClick: cancelOrdering },
        apply: {
          label: "Save",
          onClick: saveOrder,
          type: "primary",
          disabled: !orderDirty || savingOrder,
          loading: savingOrder,
        },
      }
      : {
        title: "Subject profile",
        // "Edit order" then "Add".
        apply: canManage
          ? [
            { key: "edit-order", label: "Edit order", onClick: enterOrdering },
            {
              key: "add",
              label: t("subject_management.profile.add"),
              onClick: () => openProfileDrawer(true),
            },
          ]
          : [],
        search: {
          value: search,
          onHandleChange: setSearch,
        },
      }),
    [isOpen, t, search, isOrdering, orderDirty, savingOrder, canManage, cancelOrdering, saveOrder, enterOrdering]
  );

  const getScenarioData = () =>
    ApiProfileManagement.getScenarioData().then((res) => {
      setScenarioData(res.data.scenarioData);
    });

  const refreshProfiles = useCallback(() => getProfiles(apiParams), [getProfiles, apiParams]);

  useEffect(() => {
    const updatePosition = () => {
      if (!tableRef.current) return;
      const rect = tableRef.current.getBoundingClientRect();
      setBulkLeft(rect.left + rect.width / 2);
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("resize", updatePosition);
    };
  }, []);

  const handleBulkDelete = useCallback(async () => {
    if (selectedKeys.length === 0 || isBulkDeleting) return;

    setBulkDeleting(true);

    try {
      const resp = await ApiProfileManagement.removeBulk(selectedKeys);
      const { deletedIds = [], failed = [] } = resp?.data || {};
      const failedIds = failed.map((f) => f.id);

      if (deletedIds.length) {
        const plural = deletedIds.length > 1;
        openNotification?.({
          type: "success",
          message: (
            <p>
              {deletedIds.length} {plural ? "profile fields were" : "profile field was"} deleted
            </p>
          ),
        });
      }

      if (failedIds.length) {
        const plural = failedIds.length > 1;
        openNotification?.({
          type: "error",
          message: (
            <p>
              Unable to delete {failedIds.length} {plural ? "profile fields" : "profile field"}
            </p>
          ),
        });
      }

      if (!failedIds.length) {
        clearSelection();
      } else {
        setSelectedKeys(failedIds);
      }

      await getProfiles(apiParams);
    } catch (e) {
      console.error(e);
      openNotification?.({
        type: "error",
        message: <p>Failed to delete profile fields</p>,
      });
    } finally {
      setBulkDeleting(false);
    }
  }, [
    selectedKeys,
    isBulkDeleting,
    openNotification,
    clearSelection,
    setSelectedKeys,
    getProfiles,
    apiParams,
  ]);

  useEffect(() => {
    getProfiles(apiParams);
  }, [sort.field, sort.order]);

  useEffect(() => {
    debouncedGetProfiles(apiParams);
  }, [debouncedGetProfiles, apiParams]);

  useEffect(() => {
    getScenarioData();
  }, []);

  useAdminEvents({
    onProfileChanged: (payload) => {
      refreshProfiles();

      const changedIds = [
        ...(payload?.deletedIds || []),
        payload?.id,
      ].filter(Boolean);

      setSelectedProfile((prev) => {
        if (!prev?.id || !changedIds.includes(prev.id)) return prev;

        if (payload?.action === "deleted" || payload?.action === "bulk_deleted") {
          handleNavigate("/administration/profiles");
          setOpen(false);
          setEditMode(false);
          return {};
        }

        return payload?.subjectProfileField
          ? { ...prev, ...payload.subjectProfileField }
          : prev;
      });
    },
  });

  useEffect(() => {
    if (!id && mode !== "create") {
      setOpen(false);
      setSelectedProfile({});
    }
  }, [id, mode]);

  useEffect(() => {
    if (id && profiles.length) {
      const profile = profiles.find((p) => p.id === id);
      if (profile) {
        setSelectedProfile(profile);
        setOpen(true);
      }
    } else {
      setSelectedProfile({});
    }
  }, [id, profiles]);

  useEffect(() => {
    if (mode === "edit" || mode === "create") {
      setEditMode(true);
      setOpen(true);
    } else {
      setEditMode(false);
    }
    if (mode === "create") setSelectedProfile({});
  }, [mode]);

  const openProfileDrawer = (edit) => {
    handleNavigate(`/administration/profiles/${selectedProfile.id}`);
    if (edit) setEditMode(true);

    if (selectedProfile.id) {
      handleNavigate(`/administration/profiles/${selectedProfile.id}/edit`);
    } else {
      handleNavigate(`/administration/profiles/create`);
    }
    setOpen(true);
  };

  const cancelEditing = () => {
    handleNavigate(`/administration/profiles/${selectedProfile.id}`);
    setEditMode(false);
  };

  const closeProfileDrawer = () => {
    handleNavigate(`/administration/profiles`);
    setEditMode(false);
    setOpen(false);
    setSelectedProfile({});
  };

  const createProfile = (profile) => {
    setEditMode(false);
    ApiProfileManagement.createProfile(profile)
      .then((response) => {
        const subjectProfileFieldId = response.data.subjectProfileField.id;
        getProfiles(apiParams).then(() => {
          openNotification({
            type: "success",
            message: <p>{t("subject_management.profile.notification.created", { profile: profile.title })}</p>,
          });
          handleNavigate(`/administration/profiles/${subjectProfileFieldId}`);
        });
      })
      .catch((e) => {
        openNotification({
          type: "error",
          message: <p>{t(`subject_management.profile.notification.${e?.response?.data?.error?.code || "error"}`)}</p>,
        });
      });
  };

  const updateProfile = (id, profile) => {
    setEditMode(false);
    handleNavigate(`/administration/profiles/${profile.id}`);

    ApiProfileManagement.updateProfile(id, profile)
      .then(() => {
        getProfiles(apiParams).then(() => {
          openNotification({
            type: "success",
            message: <p>{t("subject_management.profile.notification.updated", { profile: profile.title })}</p>,
          });
        });
      })
      .catch((e) => {
        openNotification({
          type: "error",
          message: <p>{t(`subject_management.profile.notification.${e?.response?.data?.error?.code || "error"}`)}</p>,
        });
      });
  };

  const deleteProfile = (id, title) => {
    closeProfileDrawer();
    ApiProfileManagement.deleteProfile(id)
      .then(() => getProfiles(apiParams))
      .then(() => {
        openNotification({
          type: "success",
          message: <p>{t("subject_management.profile.notification.deleted", { profile: title })}</p>,
        });
      })
      .catch((e) => {
        openNotification({
          type: "error",
          message: <p>{t(`subject_management.profile.notification.${e?.response?.data?.error?.code || "error"}`)}</p>,
        });
      });
  };

  const handleSelectProfile = (profile) => {
    handleNavigate(`/administration/profiles/${profile.id}`);
    setSelectedProfile(profile);
    setOpen(true);
  };

  const toEditMode = () => {
    handleNavigate(`/administration/profiles/${selectedProfile.id}/edit`);
    setEditMode(true);
  };

  const openDeleteModal = () => setOpenDeleteProfileModal(true);
  const closeDeleteModal = () => setOpenDeleteProfileModal(false);
  const handleDeleteOk = () => { deleteProfile(selectedProfile.id, selectedProfile.title); closeDeleteModal(); };
  const handleDeleteCancel = () => closeDeleteModal();

  const handleTableChange = (_pagination, _filters, sorter) => {
    const fieldKey = sorter?.columnKey || sorter?.field;
    if (!fieldKey || !sorter?.order) {
      setSort({ field: null, order: null });
      return;
    }
    const mappedField = SORT_FIELDS[fieldKey] || fieldKey;
    setSort({
      field: mappedField,
      order: sorter.order === "ascend" ? "ASC" : "DESC",
    });
  };

  const inputTypeOptions = [
    { id: "message:single", name: "Single choice" },
    { id: "message:multi",  name: "Multiple choice" },
    { id: "message:input",  name: "Text input" },
    { id: "message:picker", name: "Picker" },
    { id: "message:slider", name: "Slider" },
    { id: "message:date",   name: "Date" },
    { id: "message:time",   name: "Time" },
  ];
  const statusOptions = [
    { id: "mapped",    name: "Mapped" },
    { id: "notMapped", name: "Not mapped" },
  ];

  const updateFilterValue = (value) => setFilters(value);

  return (
    <Body
      header={headerProps}
      filters={
        isOrdering ? undefined : (
          <DropdownFilters
            filtersBy={[
              { name: "Input type", id: "inputType", children: inputTypeOptions },
              { name: "Status", id: "status", children: statusOptions },
            ]}
            updateFilterValue={updateFilterValue}
          />
        )
      }
    >
      {isOrdering ? (
        <div style={{ paddingTop: 16 }}>
          <p style={{ color: "var(--text-1)", paddingBottom: 24 }}>
            Drag the fields to set the order they appear in the subject profile.
          </p>
          <ProfileOrderList items={orderList} onChange={setOrderList} />
        </div>
      ) : (
        <ProfileTable
          data={profiles}
          sort={sort}
          onTableChange={handleTableChange}
          onHandleRowClick={handleSelectProfile}
          selectionColumn={getSelectionColumn()}
          containerRef={tableRef}
          after={
            <BulkSelectionBar
              left={bulkLeft}
              count={selectedKeys.length}
              onClear={clearSelection}
              onDelete={handleBulkDelete}
              loading={isBulkDeleting}
            />
          }
        />
      )}
      <ProfileCard
        profile={selectedProfile}
        scenarioData={scenarioData}
        isOpen={isOpen}
        close={closeProfileDrawer}
        editMode={editMode}
        toEditMode={toEditMode}
        createProfile={createProfile}
        updateProfile={updateProfile}
        deleteProfile={openDeleteModal}
        cancelEditing={cancelEditing}
      />
      <DeleteModalComponent
        isModalOpen={isOpenDeleteProfileModal}
        handleOk={handleDeleteOk}
        handleCancel={handleDeleteCancel}
        title={selectedProfile.title}
      />
    </Body>
  );
};

export default ProfileManagement;
